import { RecurringTypesEnum, SubscriptionProductNamesEnum } from "dash-common";
import { SubscriptionProduct } from "gql/generated.graphql";
import { UpgradeSubscriptionModalType } from "dash-ui";
import { RecurringToggleV3 } from "../RecurringToggleV3";
import {
  filterSubscriptions,
  getSkeletonCount,
  getSkeletonVariant,
} from "../UpgradeSubscriptionModalV3/UpgradeSubscriptionModalV3.helpers";
import SubscriptionPlanCardV3 from "../SubscriptionPlanCardV3/SubscriptionPlanCardV3";
import SubscriptionPlanCardSkeleton from "../SubscriptionPlanCardV3/SubscriptionPlanCardSkeleton";
import { SubscriptionErrorFallback } from "../SubscriptionErrorFallback";
import { ISubscriptionPlansSectionProps } from "./SubscriptionPlansSection.interfaces";
import { selectApiPlanVariant } from "entities/subscription";

/**
 * Filters products for billing and pickPlan modes.
 * Filters by seeker status, API user status, and recurring type.
 * Also ensures the current user's plan is always included.
 */
function filterForBillingAndPickPlan(
  products: SubscriptionProduct[],
  isSeeker: boolean | undefined,
  isApiUser: boolean | undefined,
  recurringType: RecurringTypesEnum,
  currentProductId?: string,
  currentUserProduct?: SubscriptionProduct | null,
  showApiPlan?: boolean,
  apiPlanId?: string
): SubscriptionProduct[] {
  // If the user is grandfathered on a legacy price, swap in that legacy variant
  // (same product name + recurring type) and hide the new variant for that slot.
  const legacyGrandfatheredName =
    currentUserProduct?.legacy &&
    currentUserProduct?.recurringType === recurringType
      ? currentUserProduct.name
      : null;

  const filtered = products.filter((product) => {
    if (product.name === SubscriptionProductNamesEnum.API) {
      return (isApiUser || showApiPlan) && product._id === apiPlanId;
    }

    // API users: show only the API plan
    if (isApiUser) {
      return false;
    }

    // Legacy products only appear when they're the user's current plan.
    if (product.legacy && product._id !== currentProductId) {
      return false;
    }

    // Hide the non-legacy variant of a product the user is grandfathered on,
    // so they don't see two cards for the same tier.
    if (
      !product.legacy &&
      legacyGrandfatheredName &&
      product.name === legacyGrandfatheredName &&
      product.recurringType === recurringType
    ) {
      return false;
    }

    // Seekers: show only Direct plans matching recurring type
    if (isSeeker) {
      return (
        product.name === SubscriptionProductNamesEnum.DIRECT &&
        product.recurringType === recurringType
      );
    }

    // Regular users: show non-Direct plans matching recurring type
    return (
      product.name !== SubscriptionProductNamesEnum.DIRECT &&
      product.recurringType === recurringType
    );
  });

  // For billing mode, only add SIXMONTH plans (discontinued) if they're the current plan
  // Other plans (MONTH/YEAR) should only appear in their respective toggle sections
  if (
    currentProductId &&
    currentUserProduct &&
    currentUserProduct.recurringType === RecurringTypesEnum.SIXMONTH
  ) {
    const isCurrentInFiltered = filtered.find(
      (p) => p._id === currentProductId
    );

    if (!isCurrentInFiltered) {
      // Add current product at the end - ensure _id matches currentProductId
      const currentProduct = {
        ...currentUserProduct,
        _id: currentProductId,
      };
      return [...filtered, currentProduct];
    }
  }

  return filtered;
}

/**
 * Gets skeleton count for billing and pickPlan modes.
 */
function getSkeletonCountForBillingAndPickPlan(
  isSeeker: boolean | undefined,
  isApiUser: boolean | undefined,
  currentUserProduct?: SubscriptionProduct | null,
  showApiPlan?: boolean
): number {
  // Seekers always see 1 card (Direct)
  if (isSeeker) return 1;

  // API users always see 1 card (API)
  if (isApiUser) return 1;

  // Regular users see 3 cards (Basic, PRO, Exchange)
  // If current plan is SIXMONTH, show 4 cards (3 standard + 1 sixmonth)
  const baseCount = 3;
  const hasSixMonthPlan =
    currentUserProduct?.recurringType === RecurringTypesEnum.SIXMONTH;
  // Unlocked API users see one extra card (the API plan).
  const apiExtra = showApiPlan ? 1 : 0;
  return (hasSixMonthPlan ? baseCount + 1 : baseCount) + apiExtra;
}

export default function SubscriptionPlansSection({
  products,
  loading,
  currentProductId,
  currentUserProduct,
  isSeeker,
  isApiUser,
  showApiPlan,
  recurringType,
  onRecurringTypeChange,
  onSelectPlan,
  checkoutLoading,
  filterMode,
  modalType,
  showAllPlans,
  modalId,
  showManageLink,
  isSubscriptionActive,
  onRenewPlan,
}: ISubscriptionPlansSectionProps) {
  const apiPlan = selectApiPlanVariant(
    products,
    recurringType,
    currentProductId
  );

  // Apply filtering based on mode
  const filteredProducts =
    filterMode === "modal"
      ? filterSubscriptions({
          subscriptions: products,
          isSeeker,
          recurringType,
          modalType,
          currentProductId,
          showAllPlans,
          currentUserProduct,
        })
      : filterForBillingAndPickPlan(
          products,
          isSeeker,
          isApiUser,
          recurringType,
          currentProductId,
          currentUserProduct,
          showApiPlan,
          apiPlan.product?._id
        );

  // Calculate skeleton count based on filter mode
  const skeletonCount =
    filterMode === "modal"
      ? getSkeletonCount(
          modalType || UpgradeSubscriptionModalType.GENERAL,
          isSeeker,
          showAllPlans
        )
      : getSkeletonCountForBillingAndPickPlan(
          isSeeker,
          isApiUser,
          currentUserProduct,
          showApiPlan
        );

  // Show error if no products available after loading
  const showError = !loading && filteredProducts.length === 0;

  return (
    <div className="flex flex-col w-full items-center mb-8">
      {/* Recurring Type Toggle - API users only get it when the API plan has
          more than one cadence; hidden when showing error */}
      {(!isApiUser || apiPlan.offersCadenceChoice) && !showError && (
        <div className="my-4">
          <RecurringToggleV3
            recurringType={recurringType}
            onToggle={onRecurringTypeChange}
          />
        </div>
      )}

      {/* Error State */}
      {showError && <SubscriptionErrorFallback />}

      {/* Cards Container */}
      {!showError && (
        <div className="w-full flex flex-wrap gap-4 flex-grow justify-center items-center lg:items-stretch">
          {loading &&
            Array.from({ length: skeletonCount }).map((_, index) => (
              <SubscriptionPlanCardSkeleton
                key={index}
                variant={
                  filterMode === "modal"
                    ? getSkeletonVariant(
                        modalType || UpgradeSubscriptionModalType.GENERAL,
                        isSeeker,
                        index
                      )
                    : index === 0 && !isSeeker && !isApiUser
                    ? "basic"
                    : "premium"
                }
              />
            ))}
          {!loading &&
            filteredProducts.map((product) => (
              <SubscriptionPlanCardV3
                key={product._id}
                product={product as SubscriptionProduct}
                onSelectPlan={onSelectPlan}
                loading={checkoutLoading}
                animated
                isCurrent={
                  product._id === currentProductId &&
                  isSubscriptionActive !== false
                }
                modalId={modalId}
                showManageLink={showManageLink}
                onRenewPlan={onRenewPlan}
              />
            ))}
        </div>
      )}
    </div>
  );
}
